import Big from 'big.js';
import { DateTime } from 'luxon';
import axios from 'smg/axios';
import Batch from 'smg/batch';
import * as buttons from 'smg/buttons';
import * as hyejoo from 'smg/customer-api/hyejoo';
import * as session from 'smg/customer-api/session';
import * as html from 'smg/html';
import * as models from 'smg/models';
import { GetProofURLs } from 'smg/proofs';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { PageLoader } from '../account';
import * as carts from '../carts';
import { Relref } from '../hugo';

declare interface myWindow extends Window {
    readonly cloudFront: string;
    readonly apiRoot: string;
}
declare const window: myWindow;

export default async function main() {
    if (await session.Redirect(undefined, Relref('/login'))) {
        return;
    }

    const b = new Batch();

    const lineItems = b.get<hyejoo.LineItems>('/v2/line-items', {
        params: {
            'data': 'name,pluralName,product',
        },
    }).then(resp => {
        const m = new Map<string, models.LineItem>();
        for (const lineItem of resp.data.lineItems) {
            if (lineItem.id) {
                m.set(lineItem.id, lineItem);
            }
        }
        return m;
    });

    void b.Exec();

    const pl: PageLoader<models.Quote> = new PageLoader<models.Quote>({
        get: () => axios.get<hyejoo.Quotes>('/v2/quotes', {
            params: {
                'sort': pl.Sort(),
                'max': pl.CurrentPageSize(),
                'data': 'serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteOrders,quoteRequest,key',
                'p': pl.CurrentPage(),
                'q': pl.Search(),
            },
        }).then(resp => ({ rows: resp.data.quotes, nextPage: resp.data.nextPage })),
        draw: async quote => {
            const el = template.Clone('#quote-template');

            const serial = el.querySelector('.quote-serial') as HTMLElement;
            serial.textContent = String(quote.serial);

            const date = el.querySelector('.quote-date') as HTMLElement;
            date.textContent = DateTime.fromISO(quote.created || '').toLocaleString(DateTime.DATE_SHORT);

            const total = Big(quote.cachedTotal || 0);
            const shipping = Big(0);

            const subtotal = total.sub(shipping);

            const subtotalEls = el.querySelectorAll('.quote-subtotal');
            subtotalEls.forEach(el => el.textContent = DollarFormat(subtotal));

            const shippingEls = el.querySelectorAll('.quote-shipping');
            shippingEls.forEach(el => el.textContent = DollarFormat(shipping));

            const totalEls = el.querySelectorAll('.quote-total');
            totalEls.forEach(el => el.textContent = DollarFormat(total));

            const addToCart = el.querySelector('.quote-add-to-cart') as HTMLButtonElement;
            addToCart.addEventListener('click', () => {
                buttons.Start(addToCart);
                void carts.AddCartItem({ quoteID: quote.key ?? '' }).then(() => {
                    addToCart.classList.remove('btn-primary', 'btn-success', 'text-white');
                    addToCart.classList.add('btn-outline-success');

                    addToCart.querySelector('i')?.classList.remove('fa-shopping-cart');
                    addToCart.querySelector('i')?.classList.add('fa-check');
                }).finally(() => buttons.Stop(addToCart));
            });

            const invoice = el.querySelector('.quote-invoice') as HTMLAnchorElement;
            invoice.href = `${window.apiRoot}/v2/quotes/${quote.id || ''}/invoice`;

            const proofsContainer = el.querySelector('.quote-proofs-container') as HTMLElement;
            const proofsParent = el.querySelector('.quote-proofs-parent') as HTMLElement;
            if (quote.quoteProofs?.length) {
                for (const op of quote.quoteProofs) {
                    const p = op.proof;
                    if (!p) {
                        continue;
                    }

                    const urls = GetProofURLs(window.cloudFront, p.file || '', p.path || '');

                    const el = template.Clone('#quote-proof-template') as HTMLAnchorElement;

                    const a = el.querySelector('a') as HTMLAnchorElement;
                    a.href = urls.main;
                    a.setAttribute('data-fancybox', quote.id || '');

                    const img = a.querySelector('img') as HTMLImageElement;
                    img.src = urls.thumb;
                    img.alt = op.name || '';

                    a.appendChild(img);

                    proofsParent.appendChild(a);
                }
            } else {
                proofsContainer.parentElement?.removeChild(proofsContainer);
            }

            if (quote.quoteLineItems?.length) {
                const qtys: Record<string, number> = {};
                for (const quoteLineItem of quote.quoteLineItems) {
                    if (!quoteLineItem.quantity) {
                        continue;
                    }

                    const lineItem = (await lineItems).get(quoteLineItem.lineItemID || '');

                    if (!(quoteLineItem.lineItem || lineItem?.product)) {
                        continue;
                    }

                    const name = quoteLineItem.lineItem || lineItem?.pluralName || lineItem?.name || 'Other';
                    qtys[name] = (qtys[name] || 0) + quoteLineItem.quantity;
                }

                const summary = el.querySelector('.quote-summary') as HTMLElement;
                summary.textContent = Object.keys(qtys).map(n => `${qtys[n]} ${n}`).join(', ');
            }

            const orderHistoryContainer = el.querySelector('.quote-order-history-container') as HTMLElement;
            if (quote.quoteOrders?.length) {
                const orderHistory = el.querySelector('.quote-order-history') as HTMLElement;
                for (const quoteOrder of quote.quoteOrders) {
                    const order = quoteOrder.order;
                    const li = html.FromString(`<li><a href="${Relref('/orders')}?q=${order?.invoiceNumber || ''}" >#${order?.invoiceNumber || ''}</a> - ${DateTime.fromISO(order?.created || '').toLocaleString(DateTime.DATE_SHORT)}</li>`);
                    orderHistory.appendChild(li);
                }
            } else {
                orderHistoryContainer.parentElement?.removeChild(orderHistoryContainer);
            }

            const projectContainer = el.querySelector('.quote-project-container') as HTMLElement;
            if (quote.quoteRequest?.quoteRequestProject?.name) {
                const project = el.querySelector('.quote-project') as HTMLElement;
                project.textContent = quote.quoteRequest.quoteRequestProject.name;
            } else {
                projectContainer.parentElement?.removeChild(projectContainer);
            }

            const quoteLink = el.querySelector('.quote-link') as HTMLAnchorElement | null;
            if (quoteLink) {
                quoteLink.href = `/quote/?id=${quote.id || ''}`;
            }

            const revisionLink = el.querySelector('.quote-request-revision') as HTMLAnchorElement;
            revisionLink.href = `/revisions/?id=${quote.key || ''}`;

            return el;
        },
    });

    pl.Load();
}